import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { LuCalendarDays, LuX } from 'react-icons/lu'
import ProjectArtwork from './ProjectArtwork'

const ProjectModal = ({ project, onClose }) => {
    const dialogRef = useRef(null)

    useEffect(() => {
        if (!project) return

        const dialog = dialogRef.current
        if (!dialog.open) dialog.showModal()
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            if (dialog.open) dialog.close()
            document.body.style.overflow = previousOverflow
        }
    }, [project])

    if (!project) return null

    const closeDialog = () => {
        dialogRef.current.close()
        onClose()
    }

    const closeOnBackdrop = (event) => {
        if (event.target !== event.currentTarget) return
        const bounds = event.currentTarget.getBoundingClientRect()
        if (
            event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom
        ) {
            closeDialog()
        }
    }

    const keepFocusInDialog = (event) => {
        if (event.key !== 'Tab') return
        const controls = event.currentTarget.querySelectorAll(
            'button:not([disabled]), a[href], [tabindex="0"]'
        )
        const first = controls[0]
        const last = controls[controls.length - 1]

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    return createPortal(
        <dialog
            ref={dialogRef}
            className="project-modal"
            aria-labelledby="project-modal-title"
            aria-describedby="project-modal-description"
            onClick={closeOnBackdrop}
            onKeyDown={keepFocusInDialog}
            onCancel={(event) => {
                event.preventDefault()
                closeDialog()
            }}
        >
            <div className="project-modal__cover">
                <ProjectArtwork
                    project={project}
                    className="project-modal__artwork"
                />
                <button
                    type="button"
                    onClick={closeDialog}
                    className="project-modal__close"
                    aria-label="Close project details"
                >
                    <LuX aria-hidden="true" />
                </button>
            </div>
            <div className="project-modal__body">
                <p className="project-card__category">{project.category}</p>
                <h2 id="project-modal-title">{project.title}</h2>
                <p className="project-card__date">
                    <LuCalendarDays aria-hidden="true" /> {project.date}
                </p>
                <div className="project-modal__overview">
                    <h3>About the project</h3>
                    <p id="project-modal-description">{project.desc}</p>
                </div>
                {project.tech && (
                    <div className="project-modal__stack">
                        <h3>Built with</h3>
                        <ul
                            className="project-card__technologies"
                            aria-label="Technologies"
                        >
                            {project.tech.split(' · ').map((technology) => (
                                <li className="project-tech" key={technology}>
                                    {technology}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </dialog>,
        document.body
    )
}

export default ProjectModal
